import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import {
  CommandNames,
  CommandSource
} from 'utils/constants/swarm/swarm-command-names';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import { EAdvancedEventsLayouts } from 'interfaces/sportsbook';
import { TTeam } from 'interfaces/sportsbook-data-levels';
import { betCommand } from 'services/bet-slip';
import { FavoriteTeamBodySkeleton } from './FavoriteTeamBodySkeleton';
import { FavoriteTeamBodyTitleSkeleton } from './FavoriteTeamBodyTitlteSkeleton';
import { useAdvancedMarkets } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/useAdvancedMarkets';
import { useGetAdvancedSportsMainData } from 'newelements/Sportsbook/layouts/advanced/helpers/useGetAdvancedSportsMainData';
import { EmptyMatches } from 'components/EmptyResultBoxes/EmptyMatches';
import { GlobalIcon } from 'components/GlobalIcon';
import { AdvancedGameCardsWithMarketsFilters } from 'newcomponents/Shared/sportsbook/AdvancedMain/AdvancedGameCardsWithMarketsFilters/AdvancedGameCardsWithMarketsFilters';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setAdvancedSportsForMarketsTypes } from 'store/actions/new-sportsbook';
import { getEventIds } from 'store/selectors/betslip';
import { getFavoriteSportTeams } from 'store/selectors/fav-data';
import { getAdvancedSportsForMarketsTypes } from 'store/selectors/new-sportsbook';
import { getStreamData } from 'store/selectors/sport-data';
import { GamesListFilterByCompetitions } from '../GamesListFilterByCompetitions';
import './index.less';

type RootObject = {
  game: {
    [key: string]: {
      id: number;
    };
  };
};

export const FavoriteTeamsBody = () => {
  useAdvancedMarkets(true);
  useGetAdvancedSportsMainData();
  const location = useLocation();
  const [gameIds, setGameIds] = useState<number[] | null>(null);
  const favoriteTeams: TTeam[] = useSelector(getFavoriteSportTeams);
  const selectedEventIds = useSelector(getEventIds).map(String);
  const dispatch = useDispatch();
  const streamData = useSelector(getStreamData);
  const sportAliases = useSelector(getAdvancedSportsForMarketsTypes);
  const routeMatch = useSbRouteMatch(
    `${mountPoint(location.pathname)}/team/:sport/:teamId?`
  );

  const selectedTeam = favoriteTeams.find(
    team => team.teamId === Number(routeMatch.params.teamId)
  );

  const teamIds = favoriteTeams.map(team => team.teamId);
  const availableSports = favoriteTeams.reduce(
    (acc: TTeam['sport'][], team) => {
      if (
        acc.find(sport => {
          return sport.id === team.sport.id;
        })
      ) {
        return acc;
      }

      acc.push(team.sport);

      return acc;
    },
    [] as TTeam['sport'][]
  );

  // 'where' is typed as 'any' to allow for flexible query structures,
  // accommodating various conditions without enforcing a specific type.
  const fetchFavoriteGameIds = (rid: string, where: any) => {
    setGameIds(null);
    void betCommand(
      {
        command: CommandNames.GET,
        params: {
          source: CommandSource.BETTING,
          subscribe: true,
          what: {
            game: [['id']]
          },
          where: where
        },
        rid
      },
      rid,
      (data: RootObject) => {
        setGameIds(Object.keys(data.game).map(elem => Number(elem)));
      },
      (data: RootObject) => {
        setGameIds(prevState => {
          const newGameIds = Object.keys(data.game).map(elem => Number(elem));

          if (!prevState) {
            return newGameIds;
          }

          return newGameIds.reduce((acc, newGameId) => {
            if (acc.includes(newGameId)) {
              return acc.filter(id => id !== newGameId);
            } else {
              return [...acc, newGameId];
            }
          }, prevState);
        });
      },
      null
    );
  };

  useEffect(() => {
    if (!teamIds.length) {
      return;
    }

    if (routeMatch.params.sport === 'all') {
      const rid = RidGenerator.gForSubscribe('FAVORITE_TEAMS_GAMES_ALL_');
      const where = {
        game: {
          '@or': [
            {
              team1_id: {
                '@in': teamIds
              }
            },
            {
              team2_id: {
                '@in': teamIds
              }
            }
          ]
        }
      };

      fetchFavoriteGameIds(rid, where);
    } else if (routeMatch.params.teamId) {
      const rid = RidGenerator.gForSubscribe('FAVORITE_TEAMS_GAMES_');
      const where = {
        game: {
          '@or': [
            { team1_id: Number(routeMatch.params.teamId) },
            { team2_id: Number(routeMatch.params.teamId) }
          ]
        }
      };

      fetchFavoriteGameIds(rid, where);
    }
  }, [routeMatch.params.teamId, routeMatch.params.sport, teamIds.join('-')]);

  useEffect(() => {
    if (selectedTeam) {
      dispatch(setAdvancedSportsForMarketsTypes(selectedTeam.sport.alias));
    } else {
      availableSports.forEach(sport =>
        dispatch(setAdvancedSportsForMarketsTypes(sport.alias))
      );
    }
  }, [selectedTeam?.sport, JSON.stringify(availableSports)]);

  if (!gameIds || !availableSports.length) {
    return <FavoriteTeamBodySkeleton />;
  }

  return (
    <div className="favoriteTeamsBody">
      {selectedTeam?.sport ? (
        isMobile() ? (
          <>
            <div
              className={cc([
                'favoriteTeamsBody--sportHeader',
                {
                  'favoriteTeamsBody--sportHeader--withEmptyView':
                    isMobile() && !gameIds.length
                }
              ])}
            >
              {sportAliases[0] === selectedTeam.sport.alias ? (
                <>
                  <div className="favoriteTeamsBody--sportHeader--icon">
                    <GlobalIcon
                      lib="sports"
                      name={selectedTeam.sport.alias}
                      size={EIconSize._24}
                    />
                  </div>
                  <div className="favoriteTeamsBody--sportHeader--name">
                    {selectedTeam.sport.name}
                  </div>
                </>
              ) : (
                <FavoriteTeamBodyTitleSkeleton />
              )}
            </div>
            {!gameIds?.length ? (
              <EmptyMatches />
            ) : (
              <GamesListFilterByCompetitions gameIds={gameIds} />
            )}
          </>
        ) : (
          <AdvancedGameCardsWithMarketsFilters
            gameIds={gameIds}
            selectedSport={selectedTeam?.sport}
            layoutType={EAdvancedEventsLayouts.upcoming}
            selectedEventIds={selectedEventIds}
            streamData={streamData}
            addSportSectionInHeader
            showEmptyView={!gameIds?.length}
          />
        )
      ) : (
        availableSports.map((sport, index) =>
          isMobile() ? (
            <>
              <div className="favoriteTeamsBody--sportHeader" key={index}>
                {sportAliases.length > 1 &&
                sportAliases.includes(sport.alias) ? (
                  <>
                    <div className="favoriteTeamsBody--sportHeader--icon">
                      <GlobalIcon
                        lib="sports"
                        name={sport.alias}
                        size={EIconSize._24}
                      />
                    </div>
                    <div className="favoriteTeamsBody--sportHeader--name">
                      {sport.name}
                    </div>
                  </>
                ) : (
                  <FavoriteTeamBodyTitleSkeleton />
                )}
              </div>
              <GamesListFilterByCompetitions
                gameIds={gameIds}
                currentSport={sport}
              />
            </>
          ) : (
            <AdvancedGameCardsWithMarketsFilters
              gameIds={gameIds}
              selectedSport={sport}
              layoutType={EAdvancedEventsLayouts.upcoming}
              selectedEventIds={selectedEventIds}
              streamData={streamData}
              addSportSectionInHeader
              key={sport.id}
              showEmptyView={!gameIds.length}
            />
          )
        )
      )}
    </div>
  );
};
